import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  getLoginAuditLogs,
  getLoginAuditStats,
  exportLoginAuditLogs,
} from "../services/loginAuditService";
import { getTenants } from "../services/superAdminService";

import CustomSelect from "../components/ui/CustomSelect";
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const DEFAULT_FILTERS = {
  search: "",
  eventType: "all",
  status: "all",
  tenantId: "all",
  role: "all",
  dateFrom: "",
  dateTo: "",
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  viewer: "Read only",
};

const AVATAR_PALETTE = [
  "bg-[#eceae4] text-[#5c5b57]",
  "bg-[#f3e8e8] text-[#8b3a3a]",
  "bg-[#eee8e0] text-[#6b5c4a]",
  "bg-[#e8ecef] text-[#4a5c6b]",
  "bg-[#ebe6ef] text-[#5a4a6b]",
];

// --- helpers ----------------------------------------------------------------

const toYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getInitials = (log) => {
  const name = [log.firstName, log.lastName].filter(Boolean).join(" ").trim();
  if (name) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  }
  return (log.email || "?").slice(0, 2).toUpperCase();
};

const getAvatarColor = (seed) => {
  const value = String(seed || "");
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash + value.charCodeAt(i)) % AVATAR_PALETTE.length;
  }
  return AVATAR_PALETTE[hash];
};

const parseUserAgent = (userAgent) => {
  if (!userAgent) return { browser: "Unknown", os: "Unknown", isMobile: false };

  const ua = userAgent;
  let browser = "Unknown";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = "Chrome";
  else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = "Safari";
  else if (/Firefox\//i.test(ua)) browser = "Firefox";
  else if (/MSIE|Trident/i.test(ua)) browser = "Internet Explorer";
  else if (/PostmanRuntime|curl|axios|node-fetch/i.test(ua)) browser = "API client";

  let os = "Unknown";
  if (/Windows NT 10/i.test(ua)) os = "Windows";
  else if (/Windows/i.test(ua)) os = "Windows";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Mac OS X/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";

  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  return { browser, os, isMobile };
};

const formatAbsoluteTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatRelativeTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 172800) return "Yesterday";
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatLocation = (log) => {
  if (log.locationLabel) return log.locationLabel;
  const city = log.city;
  const region = log.region;
  const country = log.country;
  if (city && region) return `${city}, ${region}`;
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (region && country) return `${region}, ${country}`;
  if (country) return country;
  return "—";
};

const formatDeviceLabel = (device) => {
  if (!device) return "—";
  if (device.browser === "Unknown" && device.os === "Unknown") return "—";
  return `${device.browser} on ${device.os}`;
};

const getEventDisplay = (log) => {
  if (log.eventType === "logout") {
    return {
      label: "Signed out",
      pillClass: "bg-[#f0efeb] text-[#5c5b57]",
      dotClass: "bg-[#8a8984]",
      detail: null,
      detailClass: "text-[var(--muted,#6b6a66)]",
    };
  }
  if (log.status === "failed") {
    return {
      label: "Failed sign in",
      pillClass: "bg-[#fce8e6] text-[#c5221f]",
      dotClass: "bg-[#c5221f]",
      detail: log.reason || null,
      detailClass: "text-[#c5221f]",
    };
  }
  return {
    label: "Signed in",
    pillClass: "bg-[#e6f4ea] text-[#137333]",
    dotClass: "bg-[#137333]",
    detail: null,
    detailClass: "text-[var(--muted,#6b6a66)]",
  };
};

// --- small presentational pieces --------------------------------------------

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)] mb-1">
    {children}
  </p>
);

const StatCard = ({ label, value, hint, tone = "ink", active = false, onClick }) => {
  const tones = {
    ink: {
      dot: "bg-[var(--ink,#1c1c1a)]",
      value: "text-[var(--ink,#1c1c1a)]",
      ring: "ring-[var(--ink,#1c1c1a)]/20 border-[var(--ink,#1c1c1a)]/30",
    },
    accent: {
      dot: "bg-[var(--accent,#a51c1c)]",
      value: "text-[var(--accent,#a51c1c)]",
      ring: "ring-[var(--accent,#a51c1c)]/25 border-[var(--accent,#a51c1c)]/40",
    },
    warn: {
      dot: "bg-[#c47a1a]",
      value: "text-[#c47a1a]",
      ring: "ring-[#c47a1a]/25 border-[#c47a1a]/40",
    },
    success: {
      dot: "bg-[#137333]",
      value: "text-[#137333]",
      ring: "ring-[#137333]/25 border-[#137333]/40",
    },
  };
  const colors = tones[tone] || tones.ink;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-left bg-white rounded-[10px] border px-5 py-4 w-full transition-all cursor-pointer hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent,#a51c1c)]/30 ${
        active
          ? `shadow-sm ring-2 ${colors.ring}`
          : "border-[var(--line,#e4e2dc)] hover:border-[var(--line-strong,#d5d3cc)]"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${colors.dot}`} aria-hidden="true" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)]">
          {label}
        </p>
      </div>
      <p className={`text-[32px] font-semibold leading-none tracking-tight ${colors.value}`}>
        {value}
      </p>
      {hint && (
        <p className="text-[12px] text-[var(--muted,#6b6a66)] mt-2 leading-snug truncate">{hint}</p>
      )}
    </button>
  );
};

const EventStatusCell = ({ log }) => {
  const display = getEventDisplay(log);
  return (
    <div className="flex flex-col items-start gap-1 min-w-0">
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[12px] font-medium ${display.pillClass}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${display.dotClass}`} aria-hidden="true" />
        {display.label}
      </span>
      {display.detail && (
        <p className={`text-[12px] leading-snug ${display.detailClass}`}>{display.detail}</p>
      )}
    </div>
  );
};

const DetailItem = ({ label, value, valueClassName = "" }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--muted,#6b6a66)] mb-1">
      {label}
    </p>
    <p className={`text-[13px] text-[var(--ink,#1c1c1a)] break-words ${valueClassName}`}>
      {value || "—"}
    </p>
  </div>
);

const TableSkeleton = ({ rows = 8 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, index) => (
      <tr key={index} className="border-t border-[var(--line,#e4e2dc)]">
        {Array.from({ length: 6 }).map((__, cellIndex) => (
          <td key={cellIndex} className="px-4 py-4">
            <div className="h-3 bg-[#eeece7] rounded animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// --- page -------------------------------------------------------------------

const LoginAuditLogsPage = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isFirstLoad = useRef(true);

  // Debounce the search box so typing doesn't hammer the API.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((previous) =>
        previous.search === searchInput ? previous : { ...previous, search: searchInput }
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  useEffect(() => {
    let cancelled = false;
    getTenants()
      .then((response) => {
        if (cancelled) return;
        const list = response?.data?.tenants || response?.data || [];
        setTenants(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const queryFilters = useMemo(
    () => ({
      search: filters.search,
      eventType: filters.eventType,
      status: filters.status,
      tenantId: filters.tenantId,
      role: filters.role,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    }),
    [filters]
  );

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const [logsResponse, statsResponse] = await Promise.all([
          getLoginAuditLogs({ ...queryFilters, page, pageSize }),
          getLoginAuditStats(queryFilters),
        ]);

        setLogs(logsResponse?.data?.logs || []);
        if (logsResponse?.data?.pagination) {
          setPagination(logsResponse.data.pagination);
        }
        setStats(statsResponse?.data || null);
        setLastUpdated(new Date());
      } catch (loadError) {
        setError(loadError?.message || "Failed to load login audit logs");
      } finally {
        setLoading(false);
        setRefreshing(false);
        isFirstLoad.current = false;
      }
    },
    [queryFilters, page, pageSize]
  );

  useEffect(() => {
    loadData({ silent: !isFirstLoad.current });
  }, [loadData]);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  const applyQuickRange = (days) => {
    if (days === null) {
      setFilters((previous) => ({ ...previous, dateFrom: "", dateTo: "" }));
      return;
    }
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    setFilters((previous) => ({ ...previous, dateFrom: toYmd(from), dateTo: toYmd(to) }));
  };

  const clearFilters = () => {
    setSearchInput("");
    setFilters(DEFAULT_FILTERS);
  };

  const applyStatCardFilter = (card) => {
    setExpandedId(null);
    if (card === "total") {
      setFilters((previous) => ({ ...previous, eventType: "all", status: "all" }));
      return;
    }
    if (card === "success") {
      setFilters((previous) => ({ ...previous, eventType: "login", status: "success" }));
      return;
    }
    if (card === "failed") {
      setFilters((previous) => ({ ...previous, eventType: "all", status: "failed" }));
    }
  };

  const activeStatCard = useMemo(() => {
    if (filters.eventType === "login" && filters.status === "success") return "success";
    if (filters.status === "failed" && filters.eventType === "all") return "failed";
    if (filters.eventType === "all" && filters.status === "all") return "total";
    return null;
  }, [filters.eventType, filters.status]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportLoginAuditLogs(queryFilters);
    } catch (exportError) {
      setError(exportError?.message || "Failed to export login audit logs");
    } finally {
      setExporting(false);
    }
  };

  const filterByEmail = (email) => {
    if (!email) return;
    setSearchInput(email);
    setExpandedId(null);
  };

  const activeFilterCount = useMemo(
    () =>
      Object.entries(filters).filter(([key, value]) => value && value !== DEFAULT_FILTERS[key])
        .length,
    [filters]
  );

  const activeQuickRange = useMemo(() => {
    if (!filters.dateFrom && !filters.dateTo) return "all";
    const today = toYmd(new Date());
    if (filters.dateTo !== today) return null;
    const from = new Date(filters.dateFrom);
    const diffDays = Math.round((new Date(today) - from) / 86400000) + 1;
    if ([1, 7, 30].includes(diffDays)) return String(diffDays);
    return null;
  }, [filters.dateFrom, filters.dateTo]);

  const totalPages = pagination.totalPages || 1;
  const rangeStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.page * pagination.pageSize, pagination.total);

  const pageNumbers = useMemo(() => {
    const current = pagination.page || 1;
    const windowSize = 5;
    let start = Math.max(1, current - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [pagination.page, totalPages]);

  const selectClassName =
    "w-full h-[36px] border border-[var(--line-strong,#d5d3cc)] rounded-md px-2.5 py-1.5 text-[13px] text-[var(--ink,#1c1c1a)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/20 focus:border-[var(--accent,#a51c1c)]";

  const dateInputClassName =
    "h-[36px] w-[118px] border-0 bg-transparent px-2 text-[12px] text-[var(--ink,#1c1c1a)] focus:outline-none";

  return (
    <div className="w-full px-5 py-5 pb-14">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
            Super Admin
          </p>
          <h1 className="text-[26px] font-semibold text-[var(--ink,#1c1c1a)] mt-0.5 leading-tight">
            Login audit log
          </h1>
          <p className="text-[13px] text-[var(--muted,#6b6a66)] mt-1 max-w-xl">
            Every sign in attempt across all tenants, with where it came from and how long the
            session lasted.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => loadData({ silent: true })}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-1.5 h-[38px] px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-60"
            title={lastUpdated ? `Last updated ${formatAbsoluteTime(lastUpdated)}` : undefined}
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || pagination.total === 0}
            className="inline-flex items-center gap-1.5 h-[38px] px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-60"
          >
            <Download className="w-3.5 h-3.5" aria-hidden="true" />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-[var(--crit-soft,#fbe6e6)] border border-[var(--accent,#a51c1c)]/25 text-[var(--accent,#a51c1c)] rounded-lg px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss error">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-4">
        <StatCard
          label="Total events"
          value={(stats?.totalEvents ?? 0).toLocaleString()}
          hint={`${(stats?.eventsLast24h ?? 0).toLocaleString()} in the last 24 h`}
          tone="ink"
          active={activeStatCard === "total"}
          onClick={() => applyStatCardFilter("total")}
        />
        <StatCard
          label="Successful logins"
          value={(stats?.successfulLogins ?? 0).toLocaleString()}
          hint={`${(stats?.logouts ?? 0).toLocaleString()} sign-outs recorded`}
          tone="success"
          active={activeStatCard === "success"}
          onClick={() => applyStatCardFilter("success")}
        />
        <StatCard
          label="Failed attempts"
          value={(stats?.failedAttempts ?? 0).toLocaleString()}
          hint={
            stats?.totalEvents
              ? `${Math.round((stats.failedAttempts / stats.totalEvents) * 100)}% of all events`
              : "No activity yet"
          }
          tone="accent"
          active={activeStatCard === "failed"}
          onClick={() => applyStatCardFilter("failed")}
        />
      </div>

      {/* Filters — single row */}
      <div className="bg-white rounded-[10px] border border-[var(--line,#e4e2dc)] px-3 py-3 mb-2">
        <div className="flex flex-nowrap items-end gap-2 overflow-x-auto pb-0.5">
          <div className="relative flex-1 min-w-[200px] shrink">
            <FieldLabel>Search</FieldLabel>
            <div className="relative">
              <Search
                className="w-3.5 h-3.5 text-[var(--muted,#6b6a66)] absolute left-2.5 top-1/2 -translate-y-1/2"
                aria-hidden="true"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Name, email, organization, IP or location"
                className="w-full h-[36px] border border-[var(--line-strong,#d5d3cc)] rounded-md pl-8 pr-8 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--faint,#9a9a95)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/20 focus:border-[var(--accent,#a51c1c)]"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="w-[118px] shrink-0">
            <FieldLabel>Event</FieldLabel>
            <CustomSelect
              value={filters.eventType}
              onChange={(event) => updateFilter("eventType", event.target.value)}
              className={selectClassName}
            >
              <option value="all">All events</option>
              <option value="login">Sign ins</option>
              <option value="logout">Sign outs</option>
            </CustomSelect>
          </div>

          <div className="w-[112px] shrink-0">
            <FieldLabel>Status</FieldLabel>
            <CustomSelect
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
              className={selectClassName}
            >
              <option value="all">Any status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </CustomSelect>
          </div>

          <div className="w-[140px] shrink-0">
            <FieldLabel>Organization</FieldLabel>
            <CustomSelect
              value={filters.tenantId}
              onChange={(event) => updateFilter("tenantId", event.target.value)}
              className={selectClassName}
            >
              <option value="all">All</option>
              {tenants.map((tenant) => (
                <option
                  key={tenant.tenant_id || tenant.tenantId}
                  value={tenant.tenant_id || tenant.tenantId}
                >
                  {tenant.tenant_name || tenant.tenantName}
                </option>
              ))}
            </CustomSelect>
          </div>

          <div className="w-[112px] shrink-0">
            <FieldLabel>Role</FieldLabel>
            <CustomSelect
              value={filters.role}
              onChange={(event) => updateFilter("role", event.target.value)}
              className={selectClassName}
            >
              <option value="all">All</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="viewer">Read only</option>
            </CustomSelect>
          </div>

          <div className="shrink-0">
            <FieldLabel>Period</FieldLabel>
            <div className="inline-flex items-center h-[36px] rounded-md border border-[var(--line-strong,#d5d3cc)] p-0.5 bg-white">
              {[
                { label: "Today", days: 1 },
                { label: "7 days", days: 7 },
                { label: "30 days", days: 30 },
                { label: "All time", days: null },
              ].map((range) => {
                const key = range.days === null ? "all" : String(range.days);
                const isActive = activeQuickRange === key;
                return (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => applyQuickRange(range.days)}
                    className={`px-2.5 h-full text-[11px] font-medium rounded-[5px] transition-colors whitespace-nowrap ${
                      isActive
                        ? "bg-[var(--accent,#a51c1c)] text-white"
                        : "text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                    }`}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="shrink-0">
            <FieldLabel>Date range</FieldLabel>
            <div className="inline-flex items-center h-[36px] rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[var(--accent,#a51c1c)]/20 focus-within:border-[var(--accent,#a51c1c)]">
              <input
                type="date"
                value={filters.dateFrom}
                max={filters.dateTo || undefined}
                onChange={(event) => updateFilter("dateFrom", event.target.value)}
                aria-label="From date"
                className={dateInputClassName}
              />
              <span className="text-[11px] text-[var(--muted,#6b6a66)] px-0.5 select-none" aria-hidden="true">
                –
              </span>
              <input
                type="date"
                value={filters.dateTo}
                min={filters.dateFrom || undefined}
                onChange={(event) => updateFilter("dateTo", event.target.value)}
                aria-label="To date"
                className={dateInputClassName}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            className="h-[36px] shrink-0 px-3 text-[13px] font-medium text-[var(--ink,#1c1c1a)] bg-white border border-[var(--line-strong,#d5d3cc)] rounded-md hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="flex justify-end mb-3">
        <p className="text-[12px] text-[var(--muted,#6b6a66)]">
          Showing {(pagination.total || 0).toLocaleString()}{" "}
          {(pagination.total || 0) === 1 ? "event" : "events"}
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[10px] border border-[var(--line,#e4e2dc)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-[var(--line,#e4e2dc)]">
                {["User", "Role", "Organization", "Event", "Where", "When"].map((heading) => (
                  <th
                    key={heading}
                    className="px-4 py-3 text-[10px] font-semibold text-[var(--muted,#6b6a66)] uppercase tracking-[0.06em]"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            {loading ? (
              <TableSkeleton rows={8} />
            ) : logs.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="inline-flex flex-col items-center gap-2 text-[var(--muted,#6b6a66)]">
                      <Activity className="w-8 h-8" aria-hidden="true" />
                      <p className="text-sm font-medium text-[var(--ink,#1c1c1a)]">
                        No login activity found
                      </p>
                      <p className="text-xs">
                        {activeFilterCount > 0
                          ? "Try widening your filters or clearing the date range."
                          : "Sign-in and sign-out events will appear here as users authenticate."}
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {logs.map((log) => {
                  const device = parseUserAgent(log.userAgent);
                  const isExpanded = expandedId === log.id;
                  const isFailed = log.status === "failed";
                  const location = formatLocation(log);
                  const deviceLabel = formatDeviceLabel(device);

                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                        className={`border-t border-[var(--line,#e4e2dc)] cursor-pointer transition-colors ${
                          isExpanded
                            ? isFailed
                              ? "bg-[#fbf6f5]"
                              : "bg-[var(--sunken2,#fbfaf8)]"
                            : "hover:bg-[var(--sunken2,#fbfaf8)]"
                        }`}
                      >
                        <td className="px-4 py-3.5 align-middle">
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${getAvatarColor(
                                log.email
                              )}`}
                            >
                              {getInitials(log)}
                            </span>
                            <div className="min-w-0">
                              <p className="font-semibold text-[13px] text-[var(--ink,#1c1c1a)] truncate max-w-[200px]">
                                {log.fullName || log.email || "Unknown user"}
                              </p>
                              {log.fullName && (
                                <p className="text-[12px] text-[var(--muted,#6b6a66)] truncate max-w-[200px]">
                                  {log.email}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 align-middle text-[13px] text-[var(--muted,#6b6a66)] whitespace-nowrap">
                          {ROLE_LABELS[log.role] || "—"}
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <p className="font-semibold text-[13px] text-[var(--ink,#1c1c1a)] truncate max-w-[180px]">
                            {log.tenantName || (log.role === "super_admin" ? "Platform" : "—")}
                          </p>
                          {log.organizationCode && (
                            <p className="text-[11px] text-[var(--muted,#6b6a66)] tracking-wide">
                              {log.organizationCode}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <EventStatusCell log={log} />
                        </td>
                        <td className="px-4 py-3.5 align-middle">
                          <p className="text-[13px] text-[var(--ink,#1c1c1a)] truncate max-w-[180px]">
                            {location}
                          </p>
                          <p className="text-[12px] text-[var(--muted,#6b6a66)] truncate max-w-[180px]">
                            {deviceLabel}
                          </p>
                        </td>
                        <td className="px-4 py-3.5 align-middle whitespace-nowrap">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                                {formatRelativeTime(log.createdAt)}
                              </p>
                              <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                                {formatAbsoluteTime(log.createdAt)}
                              </p>
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 text-[var(--muted,#6b6a66)] transition-transform shrink-0 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                              aria-hidden="true"
                            />
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr
                          className={`border-t border-[var(--line,#e4e2dc)] ${
                            isFailed ? "bg-[#fbf6f5]" : "bg-[var(--sunken2,#fbfaf8)]"
                          }`}
                        >
                          <td colSpan={6} className="px-4 pb-4 pt-1">
                            <div className="bg-white rounded-[10px] border border-[var(--line,#e4e2dc)] px-5 py-4">
                              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-6 gap-y-4">
                                <DetailItem label="IP address" value={log.ipAddress} />
                                <DetailItem label="Device" value={deviceLabel} />
                                <DetailItem label="City" value={log.city} />
                                <DetailItem label="Region" value={log.region} />
                                <DetailItem label="Country" value={log.country} />
                                {isFailed && (
                                  <DetailItem
                                    label="Reason"
                                    value={log.reason}
                                    valueClassName="text-[#c5221f]"
                                  />
                                )}
                              </div>
                              {log.email && (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    filterByEmail(log.email);
                                  }}
                                  className="mt-4 text-[13px] font-medium text-[var(--accent,#a51c1c)] hover:underline"
                                >
                                  All activity for {log.email}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-[var(--line,#e4e2dc)] bg-[var(--sunken2,#fbfaf8)]">
          <div className="flex items-center gap-3 text-[12px] text-[var(--muted,#6b6a66)]">
            <span>
              {pagination.total === 0
                ? "No records"
                : `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${pagination.total.toLocaleString()}`}
            </span>
            <CustomSelect
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              className="border border-[var(--line-strong,#d5d3cc)] rounded-md px-2 py-1 text-[12px] text-[var(--ink,#1c1c1a)] bg-white focus:outline-none"
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} / page
                </option>
              ))}
            </CustomSelect>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={pagination.page <= 1}
              className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
              aria-label="First page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={pagination.page <= 1}
              className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {pageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
                className={`min-w-[30px] px-2 py-1 text-[12px] font-medium rounded-md border transition-colors ${
                  pageNumber === pagination.page
                    ? "border-[var(--accent,#a51c1c)] bg-[var(--accent,#a51c1c)] text-white"
                    : "border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                }`}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={pagination.page >= totalPages}
              className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={pagination.page >= totalPages}
              className="p-1.5 rounded-md border border-[var(--line,#e4e2dc)] bg-white text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40 disabled:hover:bg-white"
              aria-label="Last page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginAuditLogsPage;
